// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import useResizeObserver from "@react-hook/resize-observer";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useMutation as rqUseMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useMutation, useQuery } from "@apollo/client";
import { CreateNewFolder, Domain, Folder, FolderSpecial } from "@material-ui/icons";
import {
  mdiFolderHidden,
  mdiInformation,
  mdiLightbulbVariantOutline,
  mdiRefresh,
  mdiTrendingDown
} from "@mdi/js";
import { Button, Dropdown, MenuProps, Spin, Tooltip, Tree } from "antd";
import { AntTreeNodeProps } from "antd/es/tree";
import { AxiosError } from "axios";
import { EVA_RESEARCH_NAME, IS_INTERNAL_ENV } from "constants/app.constants";
import { FORECAST_FOLDER_EXPANSION_LIMIT } from "constants/forecasts.constants";
import {
  ForecastActivityType,
  MCDANIEL_FOLDER_ID,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FOLDER_ID,
  NOVI_SEARCH_FOLDER_NAME,
  SYNC_DB_FOLDER_NAME,
  TYPE_WELLS,
  USER_ARPS,
  WORKING_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_NAME
} from "constants/settings.constants";
import debounce from "lodash/debounce";
import _unique from "lodash/uniqBy";
import {
  ICheckedForecast,
  SPECIAL_FOLDERS_ID,
  SPECIAL_FOLDERS_TYPE,
  getSpecialFolders,
  setFilteredForecastFolders,
  setForecastFolders,
  setForecastMode,
  setHasViewItemsChanged,
  setNormalizeTypeWell,
  setUseForecastStartDate,
  setUserForecastSettings
} from "store/features";
import { ForecastMode } from "store/features/app/appSlice";
import { RootState } from "store/rootReducer";
import {
  selectCheckedForecastKeys,
  selectCheckedForecasts,
  selectCheckedTypeWellKeys,
  selectCheckedTypeWells,
  selectExpandedForecastKeys,
  selectExpandedTypeWellKeys,
  selectHasTypeWellSaveSucceeded,
  selectHasUnsavedChanges,
  selectIsCreatingTypeWell,
  selectSelectedTypeWell,
  selectSyncStateId,
  selectSyncStateType,
  selectTemporaryForecast,
  selectUseMcDanielForecasts,
  selectUseNoviForecasts,
  selectUseWorkingForecasts,
  useForecastStore
} from "stores/forecast";
import { selectUserSettingsOpen, usePanelStore } from "stores/panelStore";
import styled from "styled-components";
import { searchForecastFolders } from "utils/folderSearch";
import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";

import { useUser } from "hooks";

import { FOLDERS, FOLDERS_LIGHT, UPDATE_FOLDER_NAME } from "api/userArps";
import {
  ValNavFolderRequest,
  saveValNavFolder,
  updatePerformanceData
} from "api/userForecasts";

import { ForecastFolder, UserArpsFile, UserArpsItem } from "models/UserArpsModel";

import { UserArpsContainer } from "components/activity/shared";
import { UnsavedTypeWellChangesPopconfirm } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import {
  useForecastFolderFetcher,
  useForecastFolderUpdater,
  useTypeWellResetter,
  useTypeWellSaver,
  useTypeWellUpdater,
  useValNavTypeWellUpdater
} from "components/arps/hooks";
import { getTitle } from "components/arps/utils/getTitle";
import { BaseDivider, BaseInput, LabeledSwitch, Popover } from "components/base";
import { IconSpinner, OrgForecastFolderDashed } from "components/icons";
import { DEFAULT_DECLINE_TYPE_SETTING } from "components/user-settings/constants/userSetting.constants";
import { useUserSettings } from "components/user/hooks";

import useUndoRedo from "../../arps/hooks/useUndoRedo";
import { useProjectContext } from "../projects/context";
import { useArpsUploadWizard, useSelectedProject } from "../projects/hooks";
import AlertClickOutside from "./AlertClickOutside";
import CancelForecastFoldersButton from "./CancelForecastFoldersButton";
import ExportForecastsButton, { WellSource } from "./ExportForecastsButton";
import FolderNodeActions from "./FolderNodeActions";
import ForecastDetails from "./ForecastDetails";
import ForecastModeSelect from "./ForecastModeSelect";
import ForecastNodeActions from "./ForecastNodeActions";
import "./ProjectForecasts.scss";
import ForecastFolderRemoveButton from "./RemoveForecastFolderButton";
import TypeWellColorEditor from "./TypeWellColorEditor";
import ArpsUploadWizard from "./arps-upload-wizard/ArpsUploadWizard";
import { useForecastContext, useForecastDispatch } from "./context";
import { useAddForecastFolderList } from "./hooks/useAddForecastFolderList";
import { useCancelForecastFolderList } from "./hooks/useCancelForecastFolderList";
import { useTreeData } from "./hooks/useTypeWellTree";
import { useMoveFolder } from "./mutations/useMoveFolder";
import { useMoveForecast } from "./mutations/useMoveForecast";
import { getTypeWellUploadResync } from "./utils/TypeWellHelper";
import {
  calculateForecastSourceFolders,
  getUserFolderNamesFromCheckedItems
} from "./utils/forecastFolderCalculations";

export interface ProjectForecastsProps {
  type: ForecastActivityType;
  location: "Widget" | "Activity";
  widgetProps?: WidgetProps;
}

interface WidgetProps {
  setWidgetActiveTabKey: () => void;
}

export interface SaveResult {
  saveForecast: string;
}

export const DUPLICATE_FOLDER_ERROR = "Duplicated folder name detected.";
export const EMPTY_FOLDER_ERROR = "Folder name cannot be empty.";

// TODO BF: lift type and location up to ForecastProvider initialization, lots of prop drilling currently,
// will be easier to break this component down into smaller components/hooks
export default function ProjectForecasts({
  type,
  location,
  widgetProps
}: ProjectForecastsProps) {
  const dispatch = useDispatch();
  const forecastDispatch = useForecastDispatch();

  // ForecastStore actions for migrated fields
  const fsSetCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const fsSetCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);
  const fsSetCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const fsSetCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const fsSetCheckedGlobalTypeWells = useForecastStore(
    (s) => s.setCheckedGlobalTypeWells
  );
  const fsSetCheckedGlobalForecasts = useForecastStore(
    (s) => s.setCheckedGlobalForecasts
  );
  const fsSetExpandedTypeWellKeys = useForecastStore((s) => s.setExpandedTypeWellKeys);
  const fsSetExpandedForecastKeys = useForecastStore((s) => s.setExpandedForecastKeys);
  const fsSetUseMcDanielForecasts = useForecastStore((s) => s.setUseMcDanielForecasts);
  const fsSetUseNoviForecasts = useForecastStore((s) => s.setUseNoviForecasts);
  const fsSetUseWorkingForecasts = useForecastStore((s) => s.setUseWorkingForecasts);
  const fsSetHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const fsSetTemporaryForecast = useForecastStore((s) => s.setTemporaryForecast);

  // Type-dispatched helpers that branch to the correct ForecastStore setter
  // Memoized helpers — must be stable across renders.
  // fsSetExpandedKeys is in performSearch's deps → debouncedSearch's deps → useEffect deps.
  // If unstable, debouncedSearch fires every render and resets filteredFolders (clearing isEdit),
  // which causes the rename input to be unmounted after 300ms and save without user input.
  const fsSetCheckedKeys = useCallback(
    (t: typeof type, keys: string[]) =>
      t === TYPE_WELLS ? fsSetCheckedTypeWellKeys(keys) : fsSetCheckedForecastKeys(keys),
    [type, fsSetCheckedTypeWellKeys, fsSetCheckedForecastKeys]
  );
  const fsSetCheckedForecastsForType = useCallback(
    (t: typeof type, forecasts: ICheckedForecast[]) =>
      t === TYPE_WELLS
        ? fsSetCheckedTypeWells(forecasts)
        : fsSetCheckedForecasts(forecasts),
    [type, fsSetCheckedTypeWells, fsSetCheckedForecasts]
  );
  const fsSetExpandedKeys = useCallback(
    (t: typeof type, keys: string[]) =>
      t === TYPE_WELLS
        ? fsSetExpandedTypeWellKeys(keys)
        : fsSetExpandedForecastKeys(keys),
    [type, fsSetExpandedTypeWellKeys, fsSetExpandedForecastKeys]
  );
  const {
    nameChangeErrorId,
    nameChangeError,
    forecastNameField,
    error,
    forecastFolderNameFieldMap,
    temporaryForecastFolderList,
    isAddingFolderList
  } = useForecastContext();

  const [warning, setWarning] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [wizardUploadLoading, setWizardUploadLoading] = useState(false);
  const [dragDropLoading, setDragDropLoading] = useState(false);
  const [pendingTypeWellNode, setPendingTypeWellNode] =
    useState<Maybe<UserArpsItem>>(null);
  const [popoverOpenStates, setPopoverOpenStates] = useState<Record<string, boolean>>({});

  const COMPARE_MODE_FOLDER_LIMIT_WARNING =
    "You can select up to two folders in Forecast Compare Mode. Deselect one to add another.";

  const COMPARE_MODE_REQUIRES_TWO_FOLDERS_MESSAGE =
    "Forecast Compare Mode requires two folders to be selected";

  const userSettingsOpen = usePanelStore(selectUserSettingsOpen);

  const normalizeTypeWell = useSelector(
    (state: RootState) => state.app.normalizeTypeWell
  );
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  const useForecastStartDate = useSelector(
    (state: RootState) => state.app.useForecastStartDate
  );
  const userForecastSettings = useSelector(
    (state: RootState) => state.app.userForecastSettings
  );
  const syncWells = useSelector((state: RootState) => state.app.syncWells);

  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const checkedForecastKeys = useForecastStore(selectCheckedForecastKeys);
  const checkedTypeWells = useForecastStore(selectCheckedTypeWells);
  const checkedForecastsState = useForecastStore(selectCheckedForecasts);
  const expandedTypeWellKeys = useForecastStore(selectExpandedTypeWellKeys);
  const expandedForecastKeys = useForecastStore(selectExpandedForecastKeys);
  const typeWellFolders = useSelector((state: RootState) => state.arps.typeWellFolders);
  const forecastFoldersState = useSelector(
    (state: RootState) => state.arps.forecastFolders
  );
  const hasTypeWellSaveSucceeded = useForecastStore(selectHasTypeWellSaveSucceeded);
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const hasUnsavedTypeWellChanges = useForecastStore(selectHasUnsavedChanges);
  const useMcDanielForecasts = useForecastStore(selectUseMcDanielForecasts);
  const useNoviForecasts = useForecastStore(selectUseNoviForecasts);
  const useWorkingForecasts = useForecastStore(selectUseWorkingForecasts);
  const temporarayForecast = useForecastStore(selectTemporaryForecast);
  const isCreatingTypeWell = useForecastStore(selectIsCreatingTypeWell);

  const checkedKeys = type === TYPE_WELLS ? checkedTypeWellKeys : checkedForecastKeys;

  const checkedForecasts = type === TYPE_WELLS ? checkedTypeWells : checkedForecastsState;

  const expandedKeys = type === TYPE_WELLS ? expandedTypeWellKeys : expandedForecastKeys;
  const [expandedKeysBeforeFilter, setExpandedKeysBeforeFilter] =
    useState<Maybe<string[]>>(null);
  const forecastFolders = type === TYPE_WELLS ? typeWellFolders : forecastFoldersState;

  const expandedKeysRef = useRef(expandedKeys);
  const expandedKeysBeforeFilterRef = useRef(expandedKeysBeforeFilter);
  const [updateFolderName] = useMutation(UPDATE_FOLDER_NAME);

  const valnavFolderMutation = rqUseMutation(
    "vnfolder",
    (data: ValNavFolderRequest) => {
      return saveValNavFolder(data);
    },
    {
      onSuccess: async () => {
        await refetch();
      }
    }
  );

  const { isReadonly } = useUser();
  const { permissions: projectPermissions, selectedViewId } = useProjectContext();
  const { selectedProject: project } = useSelectedProject();

  const [searchValue, setSearchValue] = useState("");

  function handleSearch(value) {
    setSearchValue(value);
    const isFiltering = value?.length > 0;
    forecastDispatch({ payload: { isFiltering } });
  }

  const [height, setHeight] = useState(100);
  const ref = useRef(null);

  const [highlightedFolderIdForDragDrop, setHighlightedFolder] = useState(null);

  const foldersQuery = type === TYPE_WELLS ? FOLDERS : FOLDERS_LIGHT;

  const {
    loading,
    data: foldersData,
    refetch
  } = useQuery(foldersQuery, {
    variables: {
      req: {
        projectId: project?.projectId,
        type: type
      }
    },
    skip: !project?.projectId
  });

  const updatePerformanceDataMutation = rqUseMutation(
    (folderName: string) => {
      return updatePerformanceData(folderName);
    },
    {
      onError: (err: AxiosError) => {
        toast.error(err?.response?.data);
      }
    }
  );

  const { declineType: declineTypeSetting } = useUserSettings();

  const declineType = declineTypeSetting?.decline ?? DEFAULT_DECLINE_TYPE_SETTING;

  const undoRedoManager = useUndoRedo();

  const { onSaveTypeWell } = useTypeWellSaver(undoRedoManager);
  const { changeSelectedTypeWell } = useTypeWellUpdater(undoRedoManager);
  const { resetTypeWell } = useTypeWellResetter(type, undoRedoManager);

  const addForecastFolderList = useAddForecastFolderList(type, location);
  const cancelForecastFolderList = useCancelForecastFolderList();
  const arpsUploadWizard = useArpsUploadWizard();

  const { updateTypeWellDataWithParentNode } = useForecastFolderUpdater();
  const memoizedUpdateTypeWellDataWithParentNode = useCallback(
    (node) => {
      updateTypeWellDataWithParentNode(node);
    },
    [updateTypeWellDataWithParentNode]
  );

  const {
    updateFolderWithValNavData,
    onUpdateValNavTypeWell,
    valnavError,
    setValnavError
  } = useValNavTypeWellUpdater(type);

  const syncStateType = useForecastStore(selectSyncStateType);
  const syncStateId = useForecastStore(selectSyncStateId);
  const isSyncing = syncStateType === "typeWell";
  const syncingWellId = syncStateType === "typeWell" ? syncStateId : null;

  const memoizedSetValnavError = useCallback(setValnavError, [type]);

  const { getForecastFoldersFromFoldersData } = useForecastFolderFetcher(type);

  const memoizedGetForecastFoldersFromFoldersData = useCallback(
    getForecastFoldersFromFoldersData,
    [type]
  );

  const moveTypeWell = useMoveForecast(type);
  const moveFolder = useMoveFolder(type);
  const { treeData: rawTreeData } = useTreeData(type);

  /**
   * Process tree data to add className for org folders and special folders.
   * This allows CSS targeting of empty folders with ant-tree-switcher-noop.
   * - org-folder-node: For org folders (grey line if empty)
   * - special-folder-node: For special folders (always filled line)
   */
  const treeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processNode = (node: any): any => {
      const isOrgFolder = node.projectId === "organization" && node.isFolder;
      const isSpecialFolder = SPECIAL_FOLDERS_ID.includes(node.id);
      let className = node.className;
      if (isSpecialFolder) {
        className = "special-folder-node";
      } else if (isOrgFolder) {
        className = "org-folder-node";
      }
      return {
        ...node,
        className,
        children: node.children?.map(processNode)
      };
    };
    return rawTreeData.map(processNode);
  }, [rawTreeData]);

  /**
   * Checks if a folder's expansion should be disabled.
   * Returns true for org folders, special folders, or folders with >= 1000 forecasts.
   */
  const isFolderExpansionDisabled = useCallback(
    (node: {
      projectId?: string;
      children?: unknown[];
      isFolder?: boolean;
      id?: string;
    }) => {
      if (!node?.isFolder)
        return {
          isDisabled: false,
          isOrgFolder: false,
          isOverLimit: false,
          isSpecialFolder: false
        };

      const isOrgFolder = node.projectId === "organization";
      const isSpecialFolder = SPECIAL_FOLDERS_ID.includes(node.id);
      const forecastCount = node.children?.length ?? 0;
      const isOverLimit = forecastCount >= FORECAST_FOLDER_EXPANSION_LIMIT;

      return {
        isDisabled: isOrgFolder || isOverLimit || isSpecialFolder,
        isOrgFolder,
        isOverLimit,
        isSpecialFolder,
        forecastCount
      };
    },
    []
  );

  /**
   * Custom switcher icon renderer.
   * - Org folders: No chevron, no tooltip
   * - Special folders: No chevron, no tooltip
   * - User folders with >=1000 forecasts: Greyed chevron with tooltip (use default noop behavior)
   * - Regular folders with <1000 forecasts: Use default chevron behavior
   */
  const renderSwitcherIcon = useCallback(
    (props: AntTreeNodeProps) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodeData = props.data as any;
      const { isOrgFolder, isOverLimit, isSpecialFolder } =
        isFolderExpansionDisabled(nodeData);

      // For leaf nodes or non-folder items, return null to use default behavior
      if (props.isLeaf || !nodeData?.isFolder) {
        return null;
      }

      // For special folders, hide the chevron entirely (no tooltip needed)
      if (isSpecialFolder) {
        return <span className="hidden-switcher" />;
      }

      // For org folders, hide the chevron (no tooltip needed - it's clear they can't expand)
      if (isOrgFolder) {
        return <span className="hidden-switcher" />;
      }

      // For user folders over the limit, show greyed chevron with tooltip
      // Return null to use default noop styling, but wrap the tree node with tooltip class
      if (isOverLimit) {
        const tooltipMessage = "Folders with over 1,000 forecasts cannot be expanded";

        return (
          <Tooltip title={tooltipMessage} placement="right">
            <span className="disabled-folder-chevron" />
          </Tooltip>
        );
      }

      // For normal folders (<1000 forecasts), return null to use default chevron styling
      return null;
    },
    [isFolderExpansionDisabled]
  );

  const onTreeExpand = useCallback(
    (
      expandedKeysValue: string[],
      info: { node: AntTreeNodeProps; expanded: boolean }
    ) => {
      // Only check when expanding (not collapsing)
      if (info.expanded) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const expandingNode = info.node as any;
        const { isDisabled } = isFolderExpansionDisabled(expandingNode);

        if (isDisabled) {
          // Don't update expanded keys - tooltip on chevron explains why
          return;
        }
      }

      fsSetExpandedKeys(type, expandedKeysValue);
    },
    [fsSetExpandedKeys, type, isFolderExpansionDisabled]
  );

  const handleWizardLoadingChange = useCallback((isLoading: boolean) => {
    setWizardUploadLoading(isLoading);
  }, []);

  const treeRef = useRef(null);
  const forecastEditHandlerRef =
    useRef<Maybe<(event: React.MouseEvent, nodeToEdit?: UserArpsItem) => void>>(null);

  useHotkeys(
    "ctrl+s",
    () => {
      onSaveTypeWell();
    },
    [selectedTypeWell]
  );
  useHotkeys(
    "ctrl+z",
    () => {
      if (undoRedoManager.canUndo) {
        undoRedoManager.undo();
      }
    },
    [undoRedoManager.undo]
  );
  useHotkeys(
    "ctrl+y",
    () => {
      if (undoRedoManager.canRedo) {
        undoRedoManager.redo();
      }
    },
    [undoRedoManager.redo]
  );
  useHotkeys(
    "ctrl+shift+z",
    () => {
      if (undoRedoManager.canRedo) {
        undoRedoManager.redo();
      }
    },
    [undoRedoManager.redo]
  );
  useHotkeys(
    "r",
    () => {
      onUpdateValNavTypeWell(selectedTypeWell);
    },
    [selectedTypeWell]
  );
  useHotkeys(
    "esc",
    () => {
      resetTypeWell();
    },
    [selectedTypeWell]
  );

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
    expandedKeysBeforeFilterRef.current = expandedKeysBeforeFilter;
  }, [expandedKeys, expandedKeysBeforeFilter]);

  // Track previous foldersData to avoid unnecessary re-processing
  const prevFoldersDataRef = useRef<typeof foldersData>(null);

  useEffect(() => {
    if (!foldersData) {
      return;
    }

    // Skip if foldersData hasn't actually changed (same reference)
    if (prevFoldersDataRef.current === foldersData) {
      return;
    }
    prevFoldersDataRef.current = foldersData;

    const userArps = memoizedGetForecastFoldersFromFoldersData(foldersData, type);

    dispatch(setForecastFolders({ type: type, forecastFolders: userArps }));

    forecastDispatch({
      payload: {
        forecastFolderNameFieldMap: {},
        temporaryForecastFolderList: [],
        isAddingFolderList: false
      }
    });
  }, [foldersData, type, useMcDanielForecasts, userSettingsOpen]);

  // Recalculate forecast settings when mode changes
  useEffect(() => {
    if (type === TYPE_WELLS) {
      return;
    }
    if (forecastMode === ForecastMode.Compare) {
      const totalSelectedFolders = checkedKeys.length;

      // Determine max folders based on sync mode: 1 if sync is active, 2 otherwise
      const maxFoldersInCompareMode = syncWells?.isSync ? 1 : 2;

      // More than max folders selected when switching to Compare mode or when sync mode activates
      if (totalSelectedFolders > maxFoldersInCompareMode) {
        // Deselect ALL folders, clear special folders, clear forecast settings
        fsSetCheckedKeys(type, []);
        fsSetCheckedForecastsForType(type, []);
        fsSetCheckedGlobalForecasts([]);

        fsSetUseMcDanielForecasts(false);
        fsSetUseNoviForecasts(false);
        fsSetUseWorkingForecasts(false);

        const warningMessage = syncWells?.isSync
          ? "Sync mode requires only one folder to be selected for comparison."
          : COMPARE_MODE_REQUIRES_TWO_FOLDERS_MESSAGE;
        setWarning(warningMessage);

        dispatch(
          setUserForecastSettings({
            forecastSourceFolder: null,
            secondaryUserForecastSourceFolder: null,
            excludeMcDanielForecasts: true,
            reserveCategory: null,
            excludeNoviForecasts: true,
            specialForecastFolders: []
          })
        );

        return;
      }

      if (totalSelectedFolders === 0) {
        const warningMessage = syncWells?.isSync
          ? "Sync mode requires one folder to be selected for comparison."
          : COMPARE_MODE_REQUIRES_TWO_FOLDERS_MESSAGE;
        setWarning(warningMessage);
      } else if (
        totalSelectedFolders === maxFoldersInCompareMode ||
        (!syncWells?.isSync && totalSelectedFolders === 2)
      ) {
        setWarning(null);
      }
    } else if (forecastMode === ForecastMode.Trump) {
      // Clear any Compare mode warnings when switching to Trump mode
      setWarning(null);
    }

    // Get currently selected user folder names from checked forecasts
    // This includes both fully checked folders and partially checked folders (individual forecasts)
    const selectedUserFolders = getUserFolderNamesFromCheckedItems(
      checkedForecasts,
      treeData
    );

    // Also get fully checked folder nodes for Trump mode transition logic
    const selectedUserFolderNodes = treeData.filter(
      (node) =>
        checkedKeys.includes(node.key) &&
        node.isFolder &&
        !SPECIAL_FOLDERS_ID.includes(node.id)
    );

    // Handle Trump mode transition: Only keep one user folder
    if (forecastMode === ForecastMode.Trump && selectedUserFolderNodes.length > 1) {
      // Keep only the primary folder (first one based on current settings or first in list)
      const primaryFolderName = userForecastSettings.forecastSourceFolder;
      const primaryFolderNode = selectedUserFolderNodes.find(
        (node) => (node.title || node.folderName) === primaryFolderName
      );
      const folderToKeep = primaryFolderNode || selectedUserFolderNodes[0];

      // Update checked keys to only include the primary folder + special folders
      const newCheckedKeys = [folderToKeep.key];
      if (useMcDanielForecasts) newCheckedKeys.push(MCDANIEL_FOLDER_ID);
      if (useNoviForecasts) newCheckedKeys.push(NOVI_FOLDER_ID);
      if (useWorkingForecasts) newCheckedKeys.push(WORKING_FORECAST_FOLDER_ID);

      fsSetCheckedKeys(type, newCheckedKeys);

      // Update the checked forecasts to only include items from the kept folder
      // Flatten treeData to include children from folders so that it has the forecasts
      const flattenedTreeData = [
        ...treeData,
        ...treeData
          .filter((node) => node.isFolder && node.children)
          .flatMap((folder) => folder.children || [])
      ];

      const keptFolderForecasts = flattenedTreeData
        .filter(
          (node) =>
            !node.isFolder &&
            !SPECIAL_FOLDERS_ID.includes(node.id) &&
            node.folderId === folderToKeep.folderId
        )
        .map((node) => ({
          id: node.key || node.id,
          type: "forecast" as const,
          folderId: node.folderId,
          isFolder: false,
          title: node.title,
          reserveCategory: node.reserveCategory,
          wellData: node.wellData,
          uniqueID: node.uniqueID || node.key || node.id,
          folderName: node.folderName || "",
          arps: node.arps || [],
          constants: node.constants || [],
          children: []
        }));

      const updatedForecasts = [
        {
          id: folderToKeep.key || folderToKeep.id,
          type: "folder" as const,
          folderId: folderToKeep.folderId,
          isFolder: true,
          children: folderToKeep.children,
          title: folderToKeep.title,
          reserveCategory: folderToKeep.reserveCategory,
          wellData: folderToKeep.wellData,
          uniqueID: folderToKeep.uniqueID || folderToKeep.key || folderToKeep.id,
          folderName: folderToKeep.folderName || folderToKeep.title || "",
          arps: folderToKeep.arps || [],
          constants: folderToKeep.constants || []
        },
        ...keptFolderForecasts
      ];

      fsSetCheckedForecastsForType(type, updatedForecasts);
      fsSetCheckedGlobalForecasts(updatedForecasts);

      dispatch(
        setUserForecastSettings({
          forecastSourceFolder: folderToKeep.title || folderToKeep.folderName,
          secondaryUserForecastSourceFolder: null, // Clear secondary folder in Trump mode
          excludeMcDanielForecasts: !useMcDanielForecasts,
          reserveCategory: folderToKeep.reserveCategory,
          excludeNoviForecasts: !useNoviForecasts,
          specialForecastFolders: getSpecialFolders(
            useMcDanielForecasts,
            useNoviForecasts,
            useWorkingForecasts
          )
        })
      );

      return; // Exit early, the settings will be recalculated on the next render
    }

    const specialFolderCount =
      (useMcDanielForecasts ? 1 : 0) +
      (useNoviForecasts ? 1 : 0) +
      (useWorkingForecasts ? 1 : 0);

    const specialFolderNames = getSpecialFolders(
      useMcDanielForecasts,
      useNoviForecasts,
      useWorkingForecasts
    );

    // For sync mode, determine the single folder to compare against synced forecasts
    // Prefer user folder, but use special folder if no user folder is selected
    const selectedFolderForSyncComparison =
      selectedUserFolders.length > 0
        ? selectedUserFolders[0]
        : specialFolderNames.length > 0
        ? specialFolderNames[0]
        : null;

    const { primaryForecastSourceFolder, secondaryForecastSourceFolder } =
      calculateForecastSourceFolders(
        selectedUserFolders,
        forecastMode,
        specialFolderCount,
        userForecastSettings.forecastSourceFolder,
        {
          forecastSourceFolder: userForecastSettings.forecastSourceFolder,
          secondaryUserForecastSourceFolder:
            userForecastSettings.secondaryUserForecastSourceFolder
        },
        syncWells?.isSync ?? false,
        selectedFolderForSyncComparison
      );

    // Update settings if they've changed
    if (
      primaryForecastSourceFolder !== userForecastSettings.forecastSourceFolder ||
      secondaryForecastSourceFolder !==
        userForecastSettings.secondaryUserForecastSourceFolder
    ) {
      dispatch(
        setUserForecastSettings({
          forecastSourceFolder: primaryForecastSourceFolder,
          secondaryUserForecastSourceFolder: secondaryForecastSourceFolder,
          excludeMcDanielForecasts: !useMcDanielForecasts,
          reserveCategory: userForecastSettings.reserveCategory,
          excludeNoviForecasts: !useNoviForecasts,
          specialForecastFolders: getSpecialFolders(
            useMcDanielForecasts,
            useNoviForecasts,
            useWorkingForecasts
          )
        })
      );
    }
  }, [
    forecastMode,
    checkedKeys.length > 0 ? checkedKeys : null, // Only depend on checkedKeys when non-empty
    checkedForecasts,
    treeData,
    useMcDanielForecasts,
    useNoviForecasts,
    useWorkingForecasts,
    userForecastSettings.forecastSourceFolder,
    userForecastSettings.secondaryUserForecastSourceFolder,
    userForecastSettings.reserveCategory,
    syncWells?.isSync,
    dispatch,
    type
  ]);

  const performSearch = useCallback(
    (searchValue: string, currentForecastFolders: ForecastFolder[]) => {
      const currentExpandedKeys = expandedKeysRef.current;
      const currentExpandedKeysBeforeFilter = expandedKeysBeforeFilterRef.current;

      if (!searchValue || searchValue.length === 0) {
        dispatch(
          setFilteredForecastFolders({
            type: type,
            filteredForecastFolders: currentForecastFolders
          })
        );
        if (currentExpandedKeysBeforeFilter !== null) {
          fsSetExpandedKeys(type, currentExpandedKeysBeforeFilter);
          setExpandedKeysBeforeFilter(null);
        }
        return;
      }

      if (currentExpandedKeysBeforeFilter === null) {
        setExpandedKeysBeforeFilter(currentExpandedKeys);
      }

      const { filteredFolders, expandedKeys: expandedKeysFromFilter } =
        searchForecastFolders(currentForecastFolders, searchValue);
      fsSetExpandedKeys(type, expandedKeysFromFilter);
      dispatch(
        setFilteredForecastFolders({
          type: type,
          filteredForecastFolders: filteredFolders
        })
      );
    },
    [type, dispatch, fsSetExpandedKeys]
  );

  const debouncedSearch = useCallback(debounce(performSearch, 300), [performSearch]);

  useEffect(() => {
    debouncedSearch(searchValue, forecastFolders);
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, searchValue, forecastFolders]);

  const handleWizardUploadSuccess = useCallback(
    async (response, requestData) => {
      const { data } = await refetch({
        req: {
          projectId: project?.projectId,
          type: type
        }
      });

      // EVA-6256: a folders refetch doesn't refresh the Zustand snapshots the UI reads, so an
      // upload to the currently-open well leaves stale state until it's re-selected. Re-sync
      // from the freshly fetched folders — unless the user has unsaved edits in flight, which
      // we must not clobber. Reads/writes the store at call time to avoid callback dep churn.
      //   - selectedTypeWell drives the editor table
      //   - checkedGlobalTypeWells / checkedTypeWells drive the plotted curve (frozen
      //     arps/constants snapshots), so the matching entry must be replaced too
      if (type === TYPE_WELLS && data) {
        const store = useForecastStore.getState();
        const resync = getTypeWellUploadResync(
          memoizedGetForecastFoldersFromFoldersData(data, type),
          store.selectedTypeWell,
          store.hasUnsavedChanges,
          store.checkedGlobalTypeWells,
          store.checkedTypeWells
        );
        if (resync) {
          store.setSelectedTypeWell(resync.selectedTypeWell);
          store.setCheckedGlobalTypeWells(resync.checkedGlobalTypeWells);
          store.setCheckedTypeWells(resync.checkedTypeWells);
        }
      }

      forecastDispatch({ payload: { error: null } });
      memoizedSetValnavError(null);
      setHighlightedFolder(null);
      if (response?.data?.message) {
        setWarning(response.data.message);
      } else {
        setWarning(null);
      }
      updatePerformanceDataMutation.mutate(requestData.folderId);
      dispatch(setHasViewItemsChanged(true));
    },
    [
      refetch,
      project?.projectId,
      type,
      forecastDispatch,
      memoizedSetValnavError,
      updatePerformanceDataMutation,
      dispatch,
      memoizedGetForecastFoldersFromFoldersData
    ]
  );

  const handleWizardUploadError = useCallback(
    (error: unknown) => {
      const err = error as { response?: { data?: { error?: string }; status?: number } };
      if (err && err.response?.data) {
        if (err.response.status === 504) {
          forecastDispatch({
            payload: {
              error: "The server didn't respond in time. Please try again later."
            }
          });
        } else {
          forecastDispatch({ payload: { error: err.response?.data.error } });
        }
        setHighlightedFolder(null);
      } else {
        forecastDispatch({
          payload: {
            error: `Error uploading ${type === USER_ARPS ? "forecast" : "type well"}`
          }
        });
        setHighlightedFolder(null);
      }
    },
    [forecastDispatch, type]
  );

  const onAcceptFiles = useCallback((files) => {
    const filesUpload: UserArpsFile[] = files.map((file) => {
      const userArps = new UserArpsFile();
      userArps.file = file.name;
      userArps.fileUpload = file;
      userArps.source = "valnav";
      userArps.fileType = "upload";
      return userArps;
    });
    return filesUpload;
  }, []);
  const uploadValNav = useCallback(
    async (folderName, folderId, files) => {
      if (isReadonly || !projectPermissions.canEditTypeWells) {
        toast.error("Cannot upload as a read only user");
        return;
      }
      if (files.length === 0) {
        toast.error(`No files selected`);
        return;
      }

      arpsUploadWizard.openWizard(
        folderId,
        folderName,
        files.map((f) => f.fileUpload)
      );
    },
    [isReadonly, projectPermissions, arpsUploadWizard.openWizard]
  );

  const onTypeWellChecked = useCallback(
    (e) => {
      let completeFolders = e.checkedNodes.filter(
        (n) => n.isFolder !== true || (n.isFolder === true && !n.children.length)
      );
      if (searchValue) {
        const children = forecastFolders.flatMap((parent) => parent.children);
        // properly remove duplicates that caused crashing in echarts
        const previouslyCheckedFolders = children.filter(
          (folder) =>
            checkedKeys.includes(folder.id) &&
            !completeFolders.find((f) => f.id === folder.id)
        );
        completeFolders = [...completeFolders, ...previouslyCheckedFolders].filter(
          (v, i, a) => a.indexOf(v) === i
        );
        if (!e.checked) {
          if (e.node.isFolder) {
            completeFolders = completeFolders.filter((f: UserArpsItem) => {
              return (
                f.folderId !== e.node.folderId ||
                (f.reserveCategory !== e.node.reserveCategory && !!e.node.reserveCategory)
              );
            });
          } else {
            completeFolders = completeFolders.filter((f) => f.id !== e.node.id);
          }
        }
      }

      const partialFolders = e.halfCheckedKeys;
      const partialFoldersForecasts =
        partialFolders.length > 0
          ? e.checkedNodes.filter((n) =>
              partialFolders.includes(
                `${n.folderId}${n.reserveCategory ? "_" + n.reserveCategory : ""}`
              )
            )
          : [];
      const checkedItems = [...completeFolders, ...partialFoldersForecasts].filter(
        (v, i, a) => a.indexOf(v) === i
      );
      fsSetCheckedKeys(
        type,
        checkedItems.map((i) => i.id)
      );
      const found = checkedItems.map((node) => {
        //eslint-disable-next-line
        const { isChecked, key, ...rest } = node;
        const res = {
          ...rest,
          title: node.title,
          id: node.key, // Use rest.key instead of val.key
          type: node.isFolder ? "folder" : "forecast",
          isFolder: false,
          folderName: undefined
        };
        return res;
      });
      fsSetCheckedGlobalTypeWells(found);
      fsSetCheckedForecastsForType(type, found);
    },
    [
      fsSetCheckedKeys,
      fsSetCheckedGlobalTypeWells,
      fsSetCheckedForecastsForType,
      type,
      searchValue,
      forecastFolders,
      checkedKeys
    ]
  );

  const getCheckedSpecialFolders = (
    checkedFolders: string[],
    folderChecked: boolean,
    folderName: string
  ) => {
    const folders = checkedFolders ?? [];
    if (folderChecked && !folders.includes(folderName)) {
      return [...folders, folderName];
    }
    if (!folderChecked) {
      return folders.filter((f) => f !== folderName);
    }
    return folders;
  };

  function isSpecialFolder(node, event, currentCheckedKeys): boolean {
    // Special handling when the selected node is the McDaniel folder.
    const checkIfSpecialFolders = (
      node,
      checkId: SPECIAL_FOLDERS_TYPE,
      dispatchFn: () => void,
      settingsDispatchFn: (value: boolean) => void
    ) => {
      if (node.id === checkId) {
        // In Compare mode, check if adding this special folder would exceed the folder limit
        if (event.checked && forecastMode === ForecastMode.Compare) {
          const userFolderCount = checkedKeys.filter(
            (k) => !SPECIAL_FOLDERS_ID.includes(k)
          ).length;

          // Count special folders that would be selected after this action
          const currentSpecialFolders = checkedKeys.filter((k) =>
            SPECIAL_FOLDERS_ID.includes(k)
          );
          const newSpecialFolderCount = currentSpecialFolders.length + 1;

          const totalFoldersAfterAction = userFolderCount + newSpecialFolderCount;

          // When sync is active, only allow 1 folder; otherwise allow 2
          const maxFolders = syncWells?.isSync ? 1 : 2;

          if (syncWells?.isSync && totalFoldersAfterAction > maxFolders) {
            // In sync mode, auto-deselect all folders and select only this special folder

            // Calculate the special folder name for the folder being selected
            const specialFolderName =
              checkId === MCDANIEL_FOLDER_ID
                ? MCDANIEL_SEARCH_FOLDER_NAME
                : checkId === NOVI_FOLDER_ID
                ? NOVI_SEARCH_FOLDER_NAME
                : checkId === WORKING_FORECAST_FOLDER_ID
                ? WORKING_FORECAST_FOLDER_NAME
                : null;

            // Dispatch the special folder selection state first
            // This sets useMcDanielForecasts/useNoviForecasts/useWorkingForecasts to true
            dispatchFn();

            // Clear other special folders
            const otherSpecialFolders = SPECIAL_FOLDERS_ID.filter((id) => id !== checkId);
            if (
              otherSpecialFolders.includes(MCDANIEL_FOLDER_ID) &&
              checkedKeys.includes(MCDANIEL_FOLDER_ID)
            ) {
              fsSetUseMcDanielForecasts(false);
            }
            if (
              otherSpecialFolders.includes(NOVI_FOLDER_ID) &&
              checkedKeys.includes(NOVI_FOLDER_ID)
            ) {
              fsSetUseNoviForecasts(false);
            }
            if (
              otherSpecialFolders.includes(WORKING_FORECAST_FOLDER_ID) &&
              checkedKeys.includes(WORKING_FORECAST_FOLDER_ID)
            ) {
              fsSetUseWorkingForecasts(false);
            }

            // Clear all user folders from checkedKeys and checkedForecasts
            fsSetCheckedForecastsForType(type, []);

            // Set currentCheckedKeys to only this special folder (clearing all user folders)
            currentCheckedKeys = [checkId];

            // Update the checkedKeys
            fsSetCheckedKeys(type, currentCheckedKeys);

            // NOW dispatch the complete user forecast settings in ONE call
            // This ensures all the settings are updated together atomically
            dispatch(
              setUserForecastSettings({
                ...userForecastSettings,
                forecastSourceFolder: SYNC_DB_FOLDER_NAME,
                secondaryUserForecastSourceFolder: specialFolderName,
                excludeMcDanielForecasts: checkId !== MCDANIEL_FOLDER_ID,
                excludeNoviForecasts: checkId !== NOVI_FOLDER_ID,
                reserveCategory: null, // Clear reserve category when switching to special folder
                specialForecastFolders: [specialFolderName].filter(Boolean)
              })
            );

            setWarning(null);

            // Early return - we've handled everything for sync + compare mode
            return true;
          } else if (!syncWells?.isSync && totalFoldersAfterAction > maxFolders) {
            // In normal compare mode (non-sync), prevent selection and show warning
            setWarning(COMPARE_MODE_FOLDER_LIMIT_WARNING);
            return true; // Return true to indicate this was a special folder (but prevent action)
          }
        }

        dispatchFn();
        if (event.checked) {
          if (!currentCheckedKeys) {
            currentCheckedKeys = [...checkedKeys, checkId];
          } else if (!currentCheckedKeys.includes(checkId)) {
            currentCheckedKeys = [...currentCheckedKeys, checkId];
          }
        } else {
          if (!currentCheckedKeys) {
            currentCheckedKeys = checkedKeys.filter((k) => k !== checkId);
          } else {
            currentCheckedKeys = currentCheckedKeys.filter((k) => k !== checkId);
          }
        }
        fsSetCheckedKeys(type, currentCheckedKeys);
        settingsDispatchFn(event.checked);

        if (forecastMode === ForecastMode.Compare) {
          // Count user folders (non-special folders that are checked)
          const userFolderCount = currentCheckedKeys.filter(
            (k) => !SPECIAL_FOLDERS_ID.includes(k)
          ).length;

          // Count special folders after this change
          const specialFolderCount =
            (currentCheckedKeys.includes(MCDANIEL_FOLDER_ID) ? 1 : 0) +
            (currentCheckedKeys.includes(NOVI_FOLDER_ID) ? 1 : 0) +
            (currentCheckedKeys.includes(WORKING_FORECAST_FOLDER_ID) ? 1 : 0);

          const totalFolders = userFolderCount + specialFolderCount;

          // Clear warning if we're now at or below the limit
          if (totalFolders <= 2) {
            setWarning(null);
          }
        }

        return true;
      }
      return false;
    };
    if (
      checkIfSpecialFolders(
        node,
        MCDANIEL_FOLDER_ID,
        () => fsSetUseMcDanielForecasts(event.checked),
        (checked) =>
          dispatch(
            setUserForecastSettings({
              ...userForecastSettings,
              excludeMcDanielForecasts: !checked,
              specialForecastFolders: getCheckedSpecialFolders(
                userForecastSettings.specialForecastFolders,
                checked,
                MCDANIEL_SEARCH_FOLDER_NAME
              )
            })
          )
      )
    ) {
      return true;
    }

    if (
      checkIfSpecialFolders(
        node,
        NOVI_FOLDER_ID,
        () => fsSetUseNoviForecasts(event.checked),
        (checked) =>
          dispatch(
            setUserForecastSettings({
              ...userForecastSettings,
              excludeNoviForecasts: !checked,
              specialForecastFolders: getCheckedSpecialFolders(
                userForecastSettings.specialForecastFolders,
                checked,
                NOVI_SEARCH_FOLDER_NAME
              )
            })
          )
      )
    ) {
      return true;
    }
    if (
      checkIfSpecialFolders(
        node,
        WORKING_FORECAST_FOLDER_ID,
        () => fsSetUseWorkingForecasts(event.checked),
        (checked) =>
          dispatch(
            setUserForecastSettings({
              ...userForecastSettings,
              specialForecastFolders: getCheckedSpecialFolders(
                userForecastSettings.specialForecastFolders,
                checked,
                WORKING_FORECAST_FOLDER_NAME
              )
            })
          )
      )
    ) {
      return true;
    }
  }

  const onForecastChecked = useCallback(
    (event) => {
      /*
       * This function handles forecast selection/deselection with mode-specific behavior:
       *
       * Trump Mode:
       * - Only one project folder can be selected at a time (excluding special folders)
       * - Selecting a new folder automatically clears all previously selected folders
       * - Individual forecasts within the selected folder can be toggled
       *
       * Compare Mode:
       * - Users can select up to two project folders maximum (excluding special folders)
       * - Attempting to select a third folder shows a warning and prevents selection
       * - Primary/secondary folder assignment is preserved when possible during selection changes
       *
       * Special Folders:
       * - McDaniel, Novi, and Working folders are handled separately and don't count toward the folder limit
       * - They can be combined with project folders in both modes
       *
       * State Management:
       * - Updates Redux state for checked keys, forecast selections, and user forecast settings
       * - Calculates primary/secondary forecast source folders based on current mode and selection
       * - Maintains consistent tree UI state and global forecast state synchronization
       */
      let currentCheckedKeys = [...checkedKeys];
      const node = event.node;

      if (isSpecialFolder(node, event, currentCheckedKeys)) {
        return;
      }

      let found = [];
      let reserveCategory: string = null;
      let activeFolderName: string = null;

      // Track which special folders we're clearing in sync + compare mode
      let clearedMcDaniel = false;
      let clearedNovi = false;
      let clearedWorking = false;

      if (node.isFolder) {
        if (event.checked) {
          // Handle folder selection based on mode
          if (forecastMode === ForecastMode.Trump) {
            // Trump mode: Only allow one folder selection - clear all others
            currentCheckedKeys = [node.id];
            activeFolderName = node.title;
            reserveCategory = node.reserveCategory;
            // Clear any previous warnings on successful selection
            setWarning(null);
          } else if (forecastMode === ForecastMode.Compare) {
            // Compare mode: Check if sync is active
            if (syncWells?.isSync) {
              // Sync + Compare mode: Behave like Trump mode - auto-deselect all folders
              // Track which special folders we're clearing so we don't re-add them later

              // First, clear any selected special folders from ForecastStore
              if (useMcDanielForecasts) {
                clearedMcDaniel = true;
                fsSetUseMcDanielForecasts(false);
                dispatch(
                  setUserForecastSettings({
                    ...userForecastSettings,
                    excludeMcDanielForecasts: true,
                    specialForecastFolders: getCheckedSpecialFolders(
                      userForecastSettings.specialForecastFolders,
                      false,
                      MCDANIEL_SEARCH_FOLDER_NAME
                    )
                  })
                );
              }
              if (useNoviForecasts) {
                clearedNovi = true;
                fsSetUseNoviForecasts(false);
                dispatch(
                  setUserForecastSettings({
                    ...userForecastSettings,
                    excludeNoviForecasts: true,
                    specialForecastFolders: getCheckedSpecialFolders(
                      userForecastSettings.specialForecastFolders,
                      false,
                      NOVI_SEARCH_FOLDER_NAME
                    )
                  })
                );
              }
              if (useWorkingForecasts) {
                clearedWorking = true;
                fsSetUseWorkingForecasts(false);
                dispatch(
                  setUserForecastSettings({
                    ...userForecastSettings,
                    specialForecastFolders: getCheckedSpecialFolders(
                      userForecastSettings.specialForecastFolders,
                      false,
                      WORKING_FORECAST_FOLDER_NAME
                    )
                  })
                );
              }

              // Clear all folders and select only the new one
              currentCheckedKeys = [node.id];
              activeFolderName = node.title;
              reserveCategory = node.reserveCategory;
              setWarning(null);
            } else {
              // Normal Compare mode (non-sync): Enforce max 2 folder limit
              const currentUserFolders = checkedKeys.filter(
                (k) => !SPECIAL_FOLDERS_ID.includes(k)
              );
              const currentSpecialFolders = checkedKeys.filter((k) =>
                SPECIAL_FOLDERS_ID.includes(k)
              );
              const totalCurrentFolders =
                currentUserFolders.length + currentSpecialFolders.length;

              if (totalCurrentFolders >= 2) {
                // Prevent selection - show warning message
                setWarning(COMPARE_MODE_FOLDER_LIMIT_WARNING);
                return;
              }

              // Add the new folder selection
              currentCheckedKeys.push(node.id);
              activeFolderName = node.title;
              reserveCategory = node.reserveCategory;
            }
          }

          // Add forecasts to found based on mode
          let nodesToInclude;
          if (
            forecastMode === ForecastMode.Trump ||
            (forecastMode === ForecastMode.Compare && syncWells?.isSync)
          ) {
            // Trump mode OR Sync+Compare mode: Only include nodes from the currently selected folder
            nodesToInclude = event.checkedNodes.filter(
              (n) =>
                !SPECIAL_FOLDERS_ID.includes(n.id) &&
                (n.isFolder ? n.id === node.id : n.folderId === node.folderId)
            );
          } else {
            // Compare mode: Include all selected nodes
            nodesToInclude = event.checkedNodes.filter(
              (n) => !SPECIAL_FOLDERS_ID.includes(n.id)
            );
          }

          found.push(
            ...nodesToInclude.map((n) => ({
              id: n.key || n.id,
              type: n.isFolder ? "folder" : "forecast",
              folderId: n.folderId,
              children: n.children,
              title: n.title,
              uniqueID: n.uniqueID,
              reserveCategory: n.reserveCategory,
              wellData: n.wellData
            }))
          );
        } else {
          // Deselect the folder.
          currentCheckedKeys = checkedKeys.filter((k) => k !== node.id);

          // Add remaining selected user forecasts to found
          const remainingSelectedNodes = event.checkedNodes.filter(
            (n) => !SPECIAL_FOLDERS_ID.includes(n.id)
          );

          // Set reserveCategory and activeFolderName from the remaining selected folder
          if (remainingSelectedNodes.length > 0) {
            const remainingFolder = remainingSelectedNodes.find((n) => n.isFolder);
            if (remainingFolder) {
              reserveCategory = remainingFolder.reserveCategory;
              activeFolderName = remainingFolder.title;
            }
          }

          found.push(
            ...remainingSelectedNodes.map((n) => ({
              id: n.key || n.id,
              type: n.isFolder ? "folder" : "forecast",
              folderId: n.folderId,
              children: n.children,
              title: n.title,
              uniqueID: n.uniqueID,
              reserveCategory: n.reserveCategory,
              wellData: n.wellData
            }))
          );
        }
      } else {
        const activeFolders = event.checkedNodes.filter(
          (n) => !SPECIAL_FOLDERS_ID.includes(n.id) && n.isFolder
        );
        const selectedFolders = [];
        selectedFolders.push(...event.halfCheckedKeys);
        selectedFolders.push(...activeFolders.map((n) => n.id));

        if (selectedFolders.length === 0) {
          // no forecasts are selected
          currentCheckedKeys = [];
        } else if (selectedFolders.length === 1) {
          activeFolderName = node.folderName;
          reserveCategory = node.reserveCategory;
          if (event.checked) {
            // A new forecast was selected in the same folder.
            currentCheckedKeys = [...currentCheckedKeys, node.id];
            found.push(
              ...event.checkedNodes
                .filter((n) => !SPECIAL_FOLDERS_ID.includes(n.id))
                .map((n) => ({
                  id: n.id,
                  type: n.isFolder ? "folder" : "forecast",
                  folderId: n.folderId,
                  children: n.children,
                  wellData: n.wellData,
                  title: n.title,
                  uniqueID: n.uniqueID
                }))
            );
          } else {
            if (activeFolders.length !== selectedFolders.length) {
              // The folder is moving from a fully selected state to a partial
              currentCheckedKeys = event.checkedNodes
                .filter((n) => !SPECIAL_FOLDERS_ID.includes(n.id))
                .map((n) => n.id);
              found.push(
                ...event.checkedNodes
                  .filter((n) => !SPECIAL_FOLDERS_ID.includes(n.id))
                  .map((n) => {
                    return {
                      id: n.id,
                      type: n.isFolder ? "folder" : "forecast",
                      folderId: n.folderId,
                      children: n.children,
                      wellData: n.wellData,
                      title: n.title,
                      uniqueID: n.uniqueID
                    };
                  })
              );
            } else {
              // A forecast was deselected in the folder
              currentCheckedKeys.splice(currentCheckedKeys.indexOf(node.id), 1);
            }
          }
        }
      }
      // Do not include McDaniel in the checked forecast
      found = found.filter((f) => !SPECIAL_FOLDERS_ID.includes(f.id));

      // Add special folders back to currentCheckedKeys if they're still selected
      // BUT: Don't re-add them if we just cleared them in sync + compare mode above
      if (
        useMcDanielForecasts &&
        !clearedMcDaniel &&
        currentCheckedKeys.indexOf(MCDANIEL_FOLDER_ID) === -1
      ) {
        currentCheckedKeys.push(MCDANIEL_FOLDER_ID);
      }
      if (
        useNoviForecasts &&
        !clearedNovi &&
        currentCheckedKeys.indexOf(NOVI_FOLDER_ID) === -1
      ) {
        currentCheckedKeys.push(NOVI_FOLDER_ID);
      }
      if (
        useWorkingForecasts &&
        !clearedWorking &&
        currentCheckedKeys.indexOf(WORKING_FORECAST_FOLDER_ID) === -1
      ) {
        currentCheckedKeys.push(WORKING_FORECAST_FOLDER_ID);
      }
      fsSetCheckedKeys(type, currentCheckedKeys);
      fsSetCheckedGlobalForecasts(found);

      // Check if all folders were deselected in Compare mode
      if (forecastMode === ForecastMode.Compare && currentCheckedKeys.length === 0) {
        setWarning(COMPARE_MODE_REQUIRES_TWO_FOLDERS_MESSAGE);
      } else if (
        forecastMode === ForecastMode.Compare &&
        currentCheckedKeys.length === 2
      ) {
        setWarning(null);
      }

      // Calculate primary and secondary forecast source folders based on mode and selection
      const specialFolderCount =
        (useMcDanielForecasts ? 1 : 0) +
        (useNoviForecasts ? 1 : 0) +
        (useWorkingForecasts ? 1 : 0);

      const specialFolderNames = getSpecialFolders(
        useMcDanielForecasts,
        useNoviForecasts,
        useWorkingForecasts
      );

      // Extract folder names from the found items (includes both fully and partially checked folders)
      const userFolderNamesFromFound = getUserFolderNamesFromCheckedItems(
        found,
        treeData
      );

      // For sync mode, determine the single folder to compare against synced forecasts
      // Prefer user folder, but use special folder if no user folder is selected
      const selectedFolderForSyncComparison =
        userFolderNamesFromFound.length > 0
          ? userFolderNamesFromFound[0]
          : specialFolderNames.length > 0
          ? specialFolderNames[0]
          : null;

      // Only pass activeFolderName as currentPrimaryFolder in Trump mode or when it should take priority
      const shouldUseActiveFolderAsPrimary =
        forecastMode === ForecastMode.Trump ||
        userFolderNamesFromFound.length === 1 ||
        (userFolderNamesFromFound.length === 2 &&
          specialFolderCount === 0 &&
          node.isFolder &&
          event.checked);

      const { primaryForecastSourceFolder, secondaryForecastSourceFolder } =
        calculateForecastSourceFolders(
          userFolderNamesFromFound,
          forecastMode,
          specialFolderCount,
          shouldUseActiveFolderAsPrimary ? activeFolderName : undefined,
          {
            forecastSourceFolder: userForecastSettings.forecastSourceFolder,
            secondaryUserForecastSourceFolder:
              userForecastSettings.secondaryUserForecastSourceFolder
          },
          syncWells?.isSync ?? false,
          selectedFolderForSyncComparison
        );

      dispatch(
        setUserForecastSettings({
          forecastSourceFolder: primaryForecastSourceFolder,
          secondaryUserForecastSourceFolder: secondaryForecastSourceFolder,
          excludeMcDanielForecasts: !useMcDanielForecasts,
          reserveCategory: reserveCategory,
          excludeNoviForecasts: !useNoviForecasts,
          specialForecastFolders: getSpecialFolders(
            useMcDanielForecasts,
            useNoviForecasts,
            useWorkingForecasts
          )
        })
      );
      //checkedforecast is immutable here because dispatch above
      fsSetCheckedForecastsForType(type, found);
    },
    [
      checkedKeys,
      dispatch,
      fsSetCheckedKeys,
      fsSetCheckedGlobalForecasts,
      fsSetCheckedForecastsForType,
      fsSetUseMcDanielForecasts,
      fsSetUseNoviForecasts,
      fsSetUseWorkingForecasts,
      type,
      useMcDanielForecasts,
      useNoviForecasts,
      useWorkingForecasts,
      forecastMode,
      treeData,
      userForecastSettings.forecastSourceFolder,
      userForecastSettings.secondaryUserForecastSourceFolder,
      syncWells?.isSync
    ]
  );

  const onCheckChanged = useCallback(
    (ids: string[], e) => {
      if (selectedViewId) {
        dispatch(setHasViewItemsChanged(true));
      }

      if (type === TYPE_WELLS) {
        onTypeWellChecked(e);
      } else {
        onForecastChecked(e);
      }
    },
    [dispatch, selectedViewId, type, onTypeWellChecked, onForecastChecked]
  );

  const updateFolder = useCallback(
    async (folder: ForecastFolder, name: string) => {
      if (!folder) {
        return;
      }

      // Clear name change error on new update request
      forecastDispatch({ payload: { nameChangeErrorId: null, nameChangeError: null } });

      if (
        forecastFolders.some(
          (f) =>
            f.folderId !== folder.folderId &&
            f.folderName === name &&
            f.parentId === folder.parentId
        )
      ) {
        // If new folder name is duplicated with existing folder, raise error message
        forecastDispatch({
          payload: {
            nameChangeErrorId: folder.id,
            nameChangeError: DUPLICATE_FOLDER_ERROR
          }
        });
        return;
      }

      const input = {
        projectId: project?.projectId,
        folderId: folder.folderId,
        parentId: folder.parentId,
        name: name,
        type: type
      };

      updateFolderName({
        variables: {
          input
        }
      })
        // eslint-disable-next-line
        .then(async () => {
          await refetch({
            req: {
              projectId: project?.projectId,
              type: type
            }
          });

          const copy: ForecastFolder = JSON.parse(JSON.stringify(folder));
          copy.name = name;
          copy.isEdit = false;

          const folders = JSON.parse(JSON.stringify(forecastFolders));
          const idx = folders.findIndex((folder) => folder.key === copy.key);
          if (idx >= 0) {
            folders.splice(idx, 1, copy);
          }

          dispatch(setForecastFolders({ type: type, forecastFolders: folders }));
          forecastDispatch({ payload: { forecastNameField: "" } });
        })
        .catch(() => {
          forecastDispatch({ payload: { error: "Unable to update folder name." } });
        });
    },
    [
      dispatch,
      forecastDispatch,
      forecastFolders,
      project,
      refetch,
      type,
      updateFolderName
    ]
  );

  useLayoutEffect(() => {
    if (!ref) {
      return;
    }
    setHeight(ref.current.getBoundingClientRect().height);
  }, [ref]);

  useResizeObserver(ref, (client) => {
    if (client.contentRect.height > 0) {
      setHeight(client.contentRect.height);
    }
  });

  const addToCheckedTypeWells = useCallback(
    (existingTws: ICheckedForecast[], newTw: UserArpsItem) => {
      const newTwToAdd: ICheckedForecast = {
        ...newTw,
        title: newTw?.title ?? selectedTypeWell.title,
        id: newTw.key,
        type: newTw.isFolder ? "folder" : "forecast",
        isFolder: false,
        folderName: undefined
      };
      const checkedItems = _unique(existingTws.concat([newTwToAdd]), (item) => item.id);
      const checkedIds = checkedItems.map((i) => i.id);

      fsSetCheckedKeys(type, checkedIds);
      fsSetCheckedForecastsForType(type, checkedItems);

      // Clear unsaved changes when switching to a different type well
      if (selectedTypeWell?.id !== newTw?.id) {
        fsSetHasUnsavedChanges(false);
      }

      // changeSelectedTypeWell already sets selectedTypeWell in ForecastStore
      changeSelectedTypeWell(newTw);
    },
    [
      dispatch,
      fsSetCheckedKeys,
      fsSetCheckedForecastsForType,
      fsSetHasUnsavedChanges,
      selectedTypeWell,
      type,
      changeSelectedTypeWell
    ]
  );

  // eslint-disable-next-line
  const folderNode = useCallback(
    (fNode) => {
      if (fNode.id === MCDANIEL_FOLDER_ID) {
        return (
          <div className="title">
            <div className="title-items title-items-folder">
              <FolderSpecial />
              <span className="title-folder title-text">{EVA_RESEARCH_NAME}</span>

              {!isReadonly && (
                <div className="edit-export">
                  <ExportForecastsButton wellSource={WellSource.McDanielResearch} />
                </div>
              )}
            </div>
          </div>
        );
      }
      if (fNode.id === WORKING_FORECAST_FOLDER_ID) {
        return (
          <div className="title">
            <div className="title-items title-items-folder">
              <FolderSpecial />
              <span className="title-folder title-text">
                {WORKING_FORECAST_FOLDER_NAME}
              </span>
            </div>
          </div>
        );
      }
      if (fNode.id === NOVI_FOLDER_ID) {
        return (
          <div className="title">
            <div className="title-items title-items-folder">
              <FolderSpecial />
              <span className="title-folder title-text">Novi Labs</span>

              {!isReadonly && (
                <div className="edit-export">
                  <ExportForecastsButton wellSource={WellSource.Novi} />
                </div>
              )}
            </div>
          </div>
        );
      }

      const isNewFolder = !!fNode.temporaryId;

      const inputTitle = isNewFolder
        ? forecastFolderNameFieldMap[fNode.temporaryId]
        : forecastNameField;

      const handleCancel = async () => {
        if (isNewFolder) {
          cancelForecastFolderList();
        } else {
          await updateFolder(fNode, fNode.folderName);
        }
      };

      const handleSave = async () => {
        if (isNewFolder) {
          // Handle multiple folder creation separately
          await addForecastFolderList.mutate();
        } else {
          await updateFolder(fNode, forecastNameField);
        }
      };

      const handleFolderNameKeyDown = async (
        e: React.KeyboardEvent<HTMLInputElement>
      ) => {
        if (e.key === "Enter") {
          handleSave();
        }
        if (e.key === "Escape") {
          handleCancel();
        }
      };

      if (fNode.isEdit && fNode.editLocation === location) {
        return (
          <div className="title">
            <div className="title-items title-items-folder">
              <Folder />
              <AlertClickOutside onOutsideClick={handleSave} />
              <BaseInput
                data-testid="forecast-folder-name-input"
                value={inputTitle}
                onChange={(value) => {
                  isNewFolder
                    ? addForecastFolderList.setNameFieldMap(fNode.temporaryId, value)
                    : forecastDispatch({ payload: { forecastNameField: value } });
                }}
                autoFocus
                onFocus={(e) => {
                  e.target.select();
                }}
                type="text"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={handleFolderNameKeyDown}
              />
              {isNewFolder && <ForecastFolderRemoveButton nodeToRemove={fNode} />}
            </div>
          </div>
        );
      }
      return (
        <div className="title">
          <div
            className="title-items title-items-folder"
            onContextMenu={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}>
            {fNode.projectId !== "organization" ? (
              (fNode.children?.length ?? 0) === 0 ? (
                <Icon path={mdiFolderHidden} size="19px" />
              ) : (
                <Folder />
              )
            ) : (fNode.children?.length ?? 0) === 0 ? (
              <OrgForecastFolderDashed size="24px" />
            ) : (
              <Domain />
            )}
            <span className="title-folder title-text">{fNode.title}</span>

            {canEdit && (
              <FolderNodeActions
                fNode={fNode}
                type={type}
                source={location}
                updateFolderWithValNavData={updateFolderWithValNavData}
                onSaveTypeWell={onSaveTypeWell}
                location={location}
                uploadValNav={uploadValNav}
                onAcceptFiles={onAcceptFiles}
                setValnavError={memoizedSetValnavError}
              />
            )}
            {fNode.reserveCategory && (
              <span className="title-reservecategory">{fNode.reserveCategory}</span>
            )}
          </div>

          {canEdit && highlightedFolderIdForDragDrop === fNode.id && (
            <div className="msg">{wizardUploadLoading && <IconSpinner />}</div>
          )}
        </div>
      );
    },
    [
      isReadonly,
      canEdit,
      location,
      forecastFolderNameFieldMap,
      forecastNameField,
      updateFolder,
      onAcceptFiles,
      highlightedFolderIdForDragDrop,
      type
    ]
  );

  const scrollToTreeNode = useCallback(
    (nodeKey) => {
      const treeInstance = treeRef.current;
      if (!treeInstance) {
        return;
      }
      treeInstance.scrollTo({ key: nodeKey, align: "top" });
    },
    [treeRef]
  );

  const handleConfirmTypeWellSwitch = useCallback(() => {
    if (!pendingTypeWellNode) return;
    // Proceed with type well switch
    if (forecastEditHandlerRef.current) {
      forecastEditHandlerRef.current(
        new MouseEvent("click") as unknown as React.MouseEvent,
        pendingTypeWellNode
      );
    }
    // Use ref to get fresh state after popup restoration
    addToCheckedTypeWells(checkedForecasts, pendingTypeWellNode);
    setPendingTypeWellNode(null);
    undoRedoManager.reset();
  }, [pendingTypeWellNode, addToCheckedTypeWells, undoRedoManager]);

  const handleCancelTypeWellSwitch = useCallback(() => {
    setPendingTypeWellNode(null);
  }, []);

  const forecastNode = useCallback(
    function (forecastTwNode, hasTypeWellSaveSucceeded) {
      // prevent new folder context menu from showing up on ForecastDetails
      function disableContextMenu(event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const handleSave = async () => {
        if (isCreatingTypeWell) {
          return;
        }
        const titleToSave =
          forecastNameField.trim() || forecastTwNode.title || "Untitled Type Well";

        const saveResult = await onSaveTypeWell(forecastTwNode, titleToSave);

        if (!saveResult?.success) {
          forecastDispatch({
            payload: {
              error: "Failed to save type well. Please check the data and try again."
            }
          });
          return;
        }
      };
      const handleCancel = async () => {
        const cloneNode = { ...forecastTwNode };
        cloneNode.isEdit = false;
        memoizedUpdateTypeWellDataWithParentNode(cloneNode);

        forecastDispatch({
          payload: { forecastNameField: cloneNode.title }
        });

        // Clear temporary forecast if it exists
        if (temporarayForecast?.id === forecastTwNode.id) {
          fsSetTemporaryForecast(null);
        }
        const saveResult = await onSaveTypeWell(cloneNode, cloneNode.title, false);

        // If save failed, show error and keep type well in edit mode
        if (!saveResult?.success) {
          forecastDispatch({
            payload: {
              error: "Failed to save type well. Please check the data and try again."
            }
          });
          return;
        }
      };

      if (forecastTwNode.isEdit && forecastTwNode.editLocation === location) {
        return (
          <div className="title">
            <div className="title-items">
              <BaseInput
                data-testid="type-well-name-input"
                value={forecastNameField}
                onChange={(value) =>
                  forecastDispatch({ payload: { forecastNameField: value } })
                }
                autoFocus
                onFocus={(e) => {
                  e.target.select();
                }}
                onBlur={handleSave}
                type="text"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    handleSave();
                  } else if (e.key === "Escape") {
                    handleCancel();
                  }
                }}
              />
            </div>
          </div>
        );
      }
      const handleClick = (event: React.MouseEvent) => {
        // Only handle type well editing when we're in TYPE_WELLS mode
        if (type === TYPE_WELLS) {
          // Check if there are unsaved changes and the clicked type well is different
          if (hasUnsavedTypeWellChanges && selectedTypeWell?.id !== forecastTwNode.id) {
            // Set pending type well to show confirmation
            setPendingTypeWellNode(forecastTwNode);
            return;
          }
          if (forecastEditHandlerRef.current) {
            forecastEditHandlerRef.current(event, forecastTwNode);
          }

          addToCheckedTypeWells(checkedForecasts, forecastTwNode);
          undoRedoManager.reset();
        }
        // For USER_ARPS (forecast mode), clicking the title doesn't trigger any special action
        // The Tree component's checkbox handles forecast selection
      };

      const isPendingSwitch = pendingTypeWellNode?.id === forecastTwNode.id;

      return (
        <TitleContainer
          className="title"
          key={forecastTwNode.id}
          isValNavFcst={forecastTwNode.source}
          selected={selectedTypeWell?.id === forecastTwNode.id}>
          <TitleItemWrapper className="title-items">
            {type == "TYPEWELLS" && (
              <TypeWellColorEditor
                forecastTwNode={forecastTwNode}
                isDisabled={!projectPermissions.canEditTypeWells}
                memoizedUpdateTypeWellDataWithParentNode={
                  memoizedUpdateTypeWellDataWithParentNode
                }
              />
            )}
            <TitleWrapper extraButtonSpace={IS_INTERNAL_ENV && forecastTwNode.source}>
              <UnsavedTypeWellChangesPopconfirm
                open={isPendingSwitch}
                placement={"right"}
                onConfirm={handleConfirmTypeWellSwitch}
                onCancel={handleCancelTypeWellSwitch}
                isReset={false}
                title="Your type well changes will show on charts, but won’t be permanent unless you save.">
                <ForecastTitleWrapper
                  title={getTitle(forecastTwNode, type)}
                  onContextMenu={disableContextMenu}
                  onClick={handleClick}>
                  {getTitle(forecastTwNode, type)}
                </ForecastTitleWrapper>
              </UnsavedTypeWellChangesPopconfirm>
              {IS_INTERNAL_ENV &&
                forecastTwNode.source &&
                !isSyncing &&
                syncingWellId !==
                  `${forecastTwNode.folderId}: ${forecastTwNode.uniqueID}` && (
                  <Button
                    className={"refreshIconButton visible-on-hover"}
                    type={"text"}
                    loading={valnavFolderMutation.isLoading}
                    onClick={() => onUpdateValNavTypeWell(forecastTwNode)}
                    shape={"circle"}
                    icon={<Icon path={mdiRefresh} size={"21px"} />}
                  />
                )}
            </TitleWrapper>

            {/* check that the current synced well matches the rendered well id */}
            {isSyncing &&
            syncingWellId === `${forecastTwNode.folderId}: ${forecastTwNode.uniqueID}` ? (
              <Spin indicator={<IconSpinner />} />
            ) : (
              forecastTwNode.projectId !== "organization" && (
                <ForecastNodeActions
                  fcstTwNode={forecastTwNode}
                  type={type}
                  source={location}
                  scrollToTreeNode={scrollToTreeNode}
                  widgetProps={widgetProps}
                  setChecked={addToCheckedTypeWells}
                  onEditHandlerReady={(handler) => {
                    forecastEditHandlerRef.current = handler;
                  }}
                />
              )
            )}

            <DetailsHoverContainer
              isPopoverOpen={popoverOpenStates[forecastTwNode.id] || false}>
              <Popover
                placement="right"
                onOpenChange={(open) => {
                  setPopoverOpenStates((prev) => ({
                    ...prev,
                    [forecastTwNode.id]: open
                  }));
                }}
                content={
                  <ForecastDetails
                    arpsItem={forecastTwNode}
                    declineType={declineType}
                    getTitle={getTitle}
                    hasTypeWellSaved={hasTypeWellSaveSucceeded}
                    type={type}
                  />
                }>
                <Icon path={mdiInformation} size={"18px"} />
              </Popover>
            </DetailsHoverContainer>
          </TitleItemWrapper>
        </TitleContainer>
      );
    },
    [
      location,
      forecastNameField,
      memoizedUpdateTypeWellDataWithParentNode,
      selectedTypeWell,
      hasUnsavedTypeWellChanges,
      pendingTypeWellNode,
      type,
      checkedForecasts,
      undoRedoManager.reset,
      declineType,
      getTitle,
      onUpdateValNavTypeWell,
      scrollToTreeNode,
      widgetProps,
      forecastEditHandlerRef,
      addToCheckedTypeWells,
      popoverOpenStates
    ]
  );

  // Resets upload errors when changing projects
  useEffect(() => {
    forecastDispatch({ payload: { error: null } });
    memoizedSetValnavError(null);
    setWarning(null);
    refetch({
      req: {
        projectId: project?.projectId,
        type: type
      }
    });
  }, [project]);

  useEffect(() => {
    setCanEdit(
      (type === USER_ARPS && projectPermissions?.canEditForecasts) ||
        (type === TYPE_WELLS && projectPermissions?.canEditTypeWells)
    );
  }, [type, projectPermissions]);

  const onMenuItemClick = useCallback(
    async (e) => {
      if (e.key === "newFolder" && projectPermissions?.canEditTypeWells) {
        await addForecastFolderList.addTemporaryFolder();
      }
    },
    [addForecastFolderList.addTemporaryFolder, projectPermissions]
  );

  // This is done to prevent rapid, unnecessary highlighting of folders when a user quickly hovers over multiple folders.
  const setHighlightedFolderDebounced = debounce(setHighlightedFolder, 100);

  const onDrop = useCallback(
    (info) => {
      const isFolder = info.dragNode?.isFolder ?? false;
      const dragKey = info.dragNode.key;

      const getTargetFolderId = () => {
        if (info.dropToGap) {
          return info.node.isFolder ? info.node.parentId : info.node.folderId;
        } else {
          if (info.node.isFolder) {
            return info.node.folderId;
          } else {
            return info.node.folderId;
          }
        }
      };

      const getDropPosition = () => {
        let position;

        if (info.dropToGap) {
          position = info.dropPosition;
        } else {
          if (info.node.isFolder) {
            return 0;
          } else {
            position = info.dropPosition + 1;
          }
        }

        const dragParent = isFolder ? info.dragNode.parentId : info.dragNode.folderId;
        const dropParent = getTargetFolderId();

        if (dragParent === dropParent) {
          const dragIndex = info.dragNode.pos.split("-").pop();
          const dropIndex = info.node.pos.split("-").pop();

          if (dragIndex && dropIndex && parseInt(dragIndex) < parseInt(dropIndex)) {
            position = Math.max(0, position - 1);
          }
        }

        return position;
      };

      const targetFolderId = getTargetFolderId() ?? "";
      const dropPosition = getDropPosition();

      const currentParentId = isFolder ? info.dragNode.parentId : info.dragNode.folderId;
      const currentPositionStr = info.dragNode.pos.split("-").pop();
      const currentPosition = currentPositionStr ? parseInt(currentPositionStr) : 0;

      const isSameParent = currentParentId === targetFolderId;
      const isSamePosition = isSameParent && currentPosition === dropPosition;

      if (isSamePosition) {
        return;
      }

      if (!isFolder && !targetFolderId) {
        return;
      }

      setDragDropLoading(true);

      if (!isFolder) {
        const oldFolderId = info.dragNode.folderId;
        const input = {
          forecastUniqueId: info.dragNode.uniqueID,
          forecastId: dragKey,
          oldFolderId: oldFolderId,
          newFolderId: targetFolderId,
          type: type,
          order: dropPosition
        };

        moveTypeWell.move(input).finally(() => {
          setDragDropLoading(false);
        });
      } else {
        const folderMoveInput = {
          folderId: info.dragNode.folderId,
          oldParentId: info.dragNode.parentId || "",
          newParentId: targetFolderId,
          type: type,
          order: dropPosition
        };

        moveFolder.move(folderMoveInput).finally(() => {
          setDragDropLoading(false);
        });
      }
    },
    [moveTypeWell, moveFolder, type, setDragDropLoading]
  );

  const renderNode = useCallback(
    (item) => {
      const hasNameChangeError =
        nameChangeErrorId &&
        (nameChangeErrorId === item.id || nameChangeErrorId === item.temporaryId);

      return (
        <TreeNodeTitle
          key={item.title}
          className={`tree-title ${
            canEdit &&
            highlightedFolderIdForDragDrop &&
            highlightedFolderIdForDragDrop === item.id &&
            item.isFolder &&
            item.projectId !== "organization"
              ? "drag-drop"
              : ""
          } ${hasNameChangeError ? "title-has-error" : ""}`}
          onDragOver={(e) => {
            // The default drag over behavior of an element is to disable dropping,
            // so in order to allow dropping the handler needs to prevent this default behavior.
            e.preventDefault();
            const canAcceptFileDrop = item.isFolder && !!item.folderId;
            if (!canAcceptFileDrop) {
              e.dataTransfer.dropEffect = "none";
            } else {
              e.dataTransfer.dropEffect = "copy";
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            const canAcceptFileDrop = item.isFolder && !!item.folderId;
            if (!canAcceptFileDrop) {
              e.dataTransfer.dropEffect = "none";
            } else {
              e.dataTransfer.dropEffect = "copy";
            }
            if (
              e.dataTransfer &&
              e.dataTransfer.items &&
              e.dataTransfer.items.length > 0 &&
              e.dataTransfer.types.includes("Files")
            ) {
              // "item.id" is unique across all folders even ones with the same names but different reserve categories.
              setHighlightedFolderDebounced(item.id);
            }
          }}
          onDrop={async (ev) => {
            ev.preventDefault();
            const canAcceptFileDrop = item.isFolder && !!item.folderId;
            if (
              canAcceptFileDrop &&
              ev.dataTransfer &&
              ev.dataTransfer.items &&
              ev.dataTransfer.items.length > 0 &&
              ev.dataTransfer.types.includes("Files")
            ) {
              const hasDirectory = Array.from(
                ev.dataTransfer.items as DataTransferItemList
              )
                .filter((i) => i.kind === "file")
                .some((i) => i.webkitGetAsEntry()?.isDirectory);
              if (hasDirectory) {
                arpsUploadWizard.openWizard(item.folderId, item.folderName, undefined, {
                  initialError: "Folder upload is not supported",
                  initialErrorInstructions:
                    "Only individual files can be uploaded. Please drag and drop a single forecast file instead of a folder."
                });
              } else {
                const files = onAcceptFiles(getFilesFromEvent(ev));
                await uploadValNav(item.folderName, item.folderId, files);
              }
            }
            setHighlightedFolder(null);
          }}
          onDragLeave={(e) => {
            // When you have nested elements, and you drag over a child element, the parent will fire an onDragLeave
            // event even though you're technically still "on" the parent.
            // To prevent this, check if the related target is a child of the event target.
            const targetBeingLeft = e.currentTarget;
            const targetBeingEntered = e.relatedTarget;
            if (!targetBeingLeft.contains(targetBeingEntered)) {
              setHighlightedFolder(null);
              // Prevent from executing after the drag operation has ended.
              // This is necessary because the debounced function might still execute and incorrectly highlight the folder.
              setHighlightedFolderDebounced.cancel();
            }
          }}>
          {item.isFolder && folderNode(item)}
          {!item.isFolder && forecastNode(item, hasTypeWellSaveSucceeded)}
        </TreeNodeTitle>
      );
    },
    [
      canEdit,
      highlightedFolderIdForDragDrop,
      nameChangeErrorId,
      onAcceptFiles,
      getFilesFromEvent,
      setHighlightedFolder,
      folderNode,
      forecastNode,
      hasTypeWellSaveSucceeded
    ]
  );

  const checkNodeCondition = useCallback(
    (node) => {
      return !node.isEdit && canEdit && type === TYPE_WELLS;
    },
    [canEdit, type]
  );

  const memoizedItems = useMemo(() => {
    return projectPermissions.canEditTypeWells ? items : [];
  }, [projectPermissions.canEditTypeWells, items]);

  const hasTemporaryFolders = useMemo(() => {
    return Object.keys(temporaryForecastFolderList).length > 0;
  }, [temporaryForecastFolderList]);

  return (
    <UserArpsContainer>
      <GridContainer>
        <div>
          {canEdit && !widgetProps && (
            <>
              <div
                className="project-forecasts_info-wrapper"
                data-testid="forecast-upload-hint">
                <div className="project-forecasts_info-icon-wrapper">
                  <Icon
                    path={mdiLightbulbVariantOutline}
                    className="project-forecasts_info-icon"
                    size={1.8}
                  />
                </div>
                <p className="project-forecasts_info-text">
                  Drag and drop a ValNav or Mosaic Excel decline file to a folder.
                </p>
              </div>
              {error && <ErrorComponent>{error}</ErrorComponent>}
              {valnavError && <ErrorComponent>{valnavError}</ErrorComponent>}
              {warning && <WarningComponent>{warning}</WarningComponent>}
              {nameChangeError && <ErrorComponent>{nameChangeError}</ErrorComponent>}
            </>
          )}
        </div>
        {type === TYPE_WELLS ? (
          <div
            className={`project-forecasts_typewell-normalize-toggle${
              widgetProps ? "-widget" : ""
            }`}>
            <LabeledSwitch
              testId="project-forecasts-normalize-toggle"
              label={{
                value: "Normalize Type Well",
                isBold: false,
                tooltip: "When toggled off, type wells will not be normalized."
              }}
              switch={{
                isChecked: normalizeTypeWell,
                onChange: (val) => {
                  dispatch(setNormalizeTypeWell(val));
                }
              }}
            />
          </div>
        ) : (
          <>
            <ForecastSelectWrapper>
              <ForecastModeSelect
                value={forecastMode}
                onChange={(value) => {
                  dispatch(setForecastMode(value));
                }}>
                <Tooltip title="Select the forecast mode">
                  <Button>
                    <Icon
                      path={mdiTrendingDown}
                      className="activity-action-icon"
                      size={1}
                    />
                  </Button>
                </Tooltip>
              </ForecastModeSelect>
              <div className="project-forecasts_start-date-toggle">
                <LabeledSwitch
                  testId="project-forecasts-forecast-toggle"
                  label={{ value: "Show from start date", isBold: false }}
                  switch={{
                    isChecked: useForecastStartDate,
                    onChange: (val) => {
                      dispatch(setUseForecastStartDate(val));
                    }
                  }}
                />
              </div>
            </ForecastSelectWrapper>
          </>
        )}
        <BaseDivider />
        <ActionBar haspadding={!canEdit}>
          <BaseInput
            type="text"
            placeholder="Filter Entity Name"
            value={searchValue}
            onChange={handleSearch}
            autoFocus
          />
          {canEdit && (
            <Actions>
              {!isAddingFolderList && hasTemporaryFolders && (
                <Tooltip placement="top" title="Cancel Folders">
                  <CancelForecastFoldersButton />
                </Tooltip>
              )}

              {!isAddingFolderList && (
                <Tooltip placement="top" title="Create New Folder">
                  <ForecastHeaderActionButton
                    data-testid="forecast-new-folder-button"
                    onClick={async (evt) => {
                      await addForecastFolderList.addTemporaryFolder();
                      evt.stopPropagation();
                      evt.preventDefault();
                    }}>
                    <CreateNewFolder fontSize="large" />
                  </ForecastHeaderActionButton>
                </Tooltip>
              )}

              {isAddingFolderList && <IconSpinner />}
            </Actions>
          )}
        </ActionBar>
        <TypeWellTreeContainer
          ref={ref}
          className="tree-wrapper"
          data-testid="forecast-tree-container">
          {(loading || dragDropLoading) && (
            <SpinnerOverlay>
              <IconSpinner />
            </SpinnerOverlay>
          )}
          {treeData.length === 0 && (
            <p className="treeListEmpty">
              To get started, create a folder and add a type well.
            </p>
          )}
          <Dropdown
            trigger={["contextMenu"]}
            menu={{
              items: memoizedItems,
              onClick: onMenuItemClick
            }}>
            <Tree
              checkable
              selectable
              showIcon={false}
              showLine={false}
              treeData={treeData}
              draggable={checkNodeCondition}
              defaultExpandedKeys={expandedKeys}
              expandedKeys={expandedKeys}
              onExpand={onTreeExpand}
              checkedKeys={checkedKeys}
              onDrop={onDrop}
              onCheck={onCheckChanged}
              height={height}
              ref={treeRef}
              titleRender={renderNode}
              switcherIcon={renderSwitcherIcon}
              motion={false}
            />
          </Dropdown>
        </TypeWellTreeContainer>
      </GridContainer>
      <ArpsUploadWizard
        type={type}
        arpsUploadWizard={arpsUploadWizard}
        onUploadSuccess={handleWizardUploadSuccess}
        onUploadError={handleWizardUploadError}
        onLoadingStateChange={handleWizardLoadingChange}
      />
    </UserArpsContainer>
  );
}

const items: MenuProps["items"] = [
  {
    label: "New Folder",
    key: "newFolder",
    icon: <CreateNewFolder />
  }
];

const DetailsHoverContainer = styled.div<{ isPopoverOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a2aaad;
  margin-right: 3px; //match with ForecastActionButton margin but account for the flex gap inbetween
  transition: all var(--duration);

  ${(props) => props.isPopoverOpen && "color: var(--color-primary);"};

  &:hover svg {
    color: var(--color-primary);
  }
`;

const ForecastHeaderActionButton = styled.button`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  margin: 0;
  width: 36px;
  height: 36px;
  cursor: pointer;
  background: transparent;
  color: ${(props) => (props.disabled ? "#ddd" : "#a2aaad")};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? "#ddd"
        : props.danger
        ? "var(--color-danger)"
        : "var(--color-primary)"};
  }

  &[disabled]:hover > svg {
    cursor: default;
  }
`;

// Has padding makes up for the missing filter button, which creates padding because of its icon size
export const ActionBar = styled.div`
  display: flex;
  text-align: left;
  padding: 0 10px;
  padding: ${(props) => (props.haspadding ? "5px 10px" : "0 10px")};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`;

export const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const GridContainer = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-rows: auto auto auto auto minmax(100px, 1fr) auto;
  grid-template-columns: minmax(0, 1fr);

  .project-forecasts_info-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    margin-left: 10px;
    margin-right: 10px;
    padding: 4px 2px;

    border: solid;
    border-color: var(--color-qoute-border-color);
    border-width: 1px;
    border-radius: var(--border-radius);
    background-color: var(--color-qoute-background);
  }

  .project-forecasts_info-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
  }

  .project-forecasts_info-icon {
    color: var(--color-icon);
  }

  .project-forecasts_info-text {
    font-size: 13px;
    font-weight: var(--fontWeightLight);
  }

  .ant-tree-checkbox {
    margin-top: 0;
    align-self: center;
  }

  .ant-tree-switcher {
    display: flex;
    justify-content: center;
    align-items: center;

    &::after {
      content: "";
      display: block;
      width: 7px;
      height: 7px;
      border-style: solid;
      border-color: var(--color-text);
      border-width: 0 1px 1px 0;
      transform: rotate(315deg);
      transition: all 0.2s;
      transform-origin: 50%;
    }

    // Hide the default ::after chevron when a hidden switcher is present
    &:has(.hidden-switcher)::after {
      display: none !important;
    }

    // Hide the default ::after chevron when a disabled folder chevron is present
    &:has(.disabled-folder-chevron)::after {
      display: none !important;
    }

    // Style the switcher container when it has a disabled folder chevron
    &:has(.disabled-folder-chevron) {
      cursor: not-allowed;
    }
  }

  .ant-tree-switcher:hover::after {
    border-color: var(--color-primary);
    align-items: center;
    justify-content: center;
  }

  .ant-tree-switcher.ant-tree-switcher_open::after {
    transform: rotate(405deg);
  }

  .ant-tree-switcher svg {
    display: none;
  }

  .ant-tree-switcher-noop {
    opacity: 0.25;
    pointer-events: none;
  }

  // Org folders: hide the default noop switcher chevron entirely
  .org-folder-node .ant-tree-switcher-noop {
    opacity: 0;
    pointer-events: none;

    &::after {
      display: none;
    }
  }

  // Special folders: hide the default noop switcher chevron entirely
  .special-folder-node .ant-tree-switcher-noop {
    opacity: 0;
    pointer-events: none;

    &::after {
      display: none;
    }
  }

  .shapefile-leaf .ant-tree-switcher-noop {
    opacity: 0;
    pointer-events: none;
  }

  // Hidden switcher style - no visible element but maintains space for tooltip
  .hidden-switcher {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
  }

  // Disabled folder chevron - greyed out chevron for user folders over limit
  .disabled-folder-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    cursor: not-allowed;

    &::after {
      content: "";
      display: block;
      width: 7px;
      height: 7px;
      border-style: solid;
      border-color: var(--color-text);
      border-width: 0 1px 1px 0;
      transform: rotate(315deg);
      opacity: 0.25;
    }
  }

  .ant-tree {
    height: 100%;
  }

  .ant-tree-list {
    width: 100%;
    height: 100%;
  }

  .ant-tree-treenode {
    display: grid;
    grid-template-columns: auto auto auto minmax(0, 1fr);
    width: 100%;

    .ant-tree-node-content-wrapper {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    }

    .ant-tree-iconEle {
      line-height: 26px;
    }

    .ant-tree-title {
      width: 100%;
    }
  }

  .ant-tree-list-scrollbar {
    .ant-tree-list-scrollbar-thumb {
      border-radius: 0 !important;
      background: var(--color-scrollbar-thumb) !important;
    }
  }
`;

const ForecastSelectWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 0 10px;
  margin-top: 8px;
  flex-direction: column; /* Add this to stack items vertically */

  > :first-child {
    flex-grow: 0;
  }
`;

const TitleItemWrapper = styled.div`
  //show button only on hover

  .refreshIconButton {
    padding: 0px !important;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .visible-on-hover {
    opacity: 0;

    &:hover {
      background: none;
      color: var(--color-primary);
    }
  }

  &:hover {
    .visible-on-hover {
      transition: all var(--duration);
      opacity: 1;
    }
  }
`;

const TypeWellTreeContainer = styled.div`
  position: relative;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
  height: 100%;

  .treeListEmpty {
    padding: 5px 15px;
  }

  .ant-tree {
    .ant-tree-treenode:hover {
      background: #f5f5f5;
    }

    .ant-tree-node-content-wrapper:hover {
      background: none;
    }
  }
`;

const SpinnerOverlay = styled.div`
  // Overlay the IconSpinner over the text and other items in the container.
  position: absolute;
  top: 0;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const TreeNodeTitle = styled.div`
  text-align: left;
  width: 100%;

  &.drag-drop {
    border: 2px dashed gray;
    padding: 0 5px;
  }

  .title {
    width: 100%;
    cursor: default;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-size: 1.4rem;

    .title-items {
      display: flex;
      gap: 4px;
      width: 100%;
      padding-right: 8px;
      align-items: center;
      white-space: nowrap;
      text-overflow: ellipsis;
      min-height: 32px;

      .title-folder {
        padding-left: 5px;
      }

      .title-text {
        display: block;
        flex-grow: 1;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .title-reservecategory {
        display: block;
        background-color: #dfdfdf;
        padding: 1px 3px;
        border-radius: 8px;
        font-size: 10px;
        font-weight: bold;
      }

      .edit-icon,
      .edit-export,
      .info-icon {
        margin-left: auto;
        display: flex;

        svg {
          flex-shrink: 0;
          opacity: 0;
          transition: all var(--duration);

          &:hover {
            color: var(--color-primary);
          }
        }
      }

      &:hover {
        .edit-icon,
        .edit-export,
        .info-icon {
          display: flex;
        }

        svg {
          opacity: 1;
        }
      }

      .input {
        height: 24px;
      }
    }

    .title-items-folder svg {
      color: #a2aaad;
      width: 18px; // to match layer tab icons
      height: 18px;
    }
  }
`;

const ErrorComponent = styled.div`
  color: red;
  width: 100%;
  max-width: fit-content;
  text-align: left;
  padding-left: 5px;
  padding-right: 5px;
`;

const WarningComponent = styled.div`
  color: var(--orange);
  width: 100%;
  max-width: fit-content;
  text-align: left;
  padding-left: 5px;
  padding-right: 5px;
`;

const TitleContainer = styled.div`
  color: ${(props) => (props.selected ? "var(--color-primary)" : "black")};
  font-weight: ${(props) => (props.selected ? "bold" : "normal")};
`;

const ForecastTitleWrapper = styled.div`
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;
const TitleWrapper = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: ${(props) => (props.extraButtonSpace ? "1fr 30px" : "1fr")};
  align-items: center;
  justify-content: flex-start;

  .ant-btn {
    padding: 5px;
    color: #a2aaad;
  }
`;
